import { Fragment, useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { useToast } from '../../context/ToastContext.jsx';
import LoadingState from '../LoadingState.jsx';
import EmptyState from '../EmptyState.jsx';
import StatusBadge from '../StatusBadge.jsx';
import Modal from '../Modal.jsx';

// Exact status vocabulary from migrations/024_supply_quality_transparency.sql's CHECK constraint
// on supply_authorizations.status -- verified against the migration, not guessed.
const STATUS_VALUES = ['CONFIGURED', 'VALID', 'VERIFIED', 'UNVERIFIED', 'CONFLICT', 'ERROR'];
const RELATIONSHIP_VALUES = ['DIRECT', 'RESELLER'];

export default function SupplyAuthorizationsTab() {
  const toast = useToast();
  const [rows, setRows] = useState(null);
  const [sellerAccounts, setSellerAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [identityType, setIdentityType] = useState('');
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [rechecking, setRechecking] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [list, sellers] = await Promise.all([
        api.listSupplyAuthorizations({ identity_type: identityType || undefined, status: status || undefined }),
        api.listSellerAccounts().catch(() => []),
      ]);
      setRows(list);
      setSellerAccounts(sellers);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load supply authorizations.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityType, status]);

  async function deleteRow(row) {
    if (!window.confirm(`Delete the supply authorization record for "${row.identity_value}"?`)) return;
    try {
      await api.deleteSupplyAuthorization(row.id);
      toast.success('Deleted.');
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete.');
    }
  }

  async function recheck(row) {
    setRechecking(row.id);
    try {
      const updated = await api.recheckSupplyAuthorization(row.id);
      toast[updated.status === 'VERIFIED' || updated.status === 'VALID' ? 'success' : 'info'](
        `${row.identity_value}: ${updated.status}${updated.last_check_result?.reason ? ` -- ${updated.last_check_result.reason}` : ''}`
      );
      load();
      setExpandedId(row.id);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Recheck failed.');
    } finally {
      setRechecking(null);
    }
  }

  return (
    <div className="stack">
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Supply authorizations</h2>
          <button className="btn btn--primary btn--sm" onClick={() => setCreating(true)}>+ Add record</button>
        </div>
        <p className="card__hint">
          Verification records of whether a supply source's OWN ads.txt/app-ads.txt actually
          authorizes BidDigi. A record here is a claim to check, not proof of authorization by
          itself -- use "Recheck" to run the real fetch.
        </p>

        <div className="toolbar">
          <select className="toolbar__search" value={identityType} onChange={(e) => setIdentityType(e.target.value)}>
            <option value="">All identity types</option>
            <option value="domain">domain (ads.txt)</option>
            <option value="bundle">bundle (app-ads.txt)</option>
          </select>
          <select className="toolbar__search" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {STATUS_VALUES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {loading && <LoadingState label="Loading supply authorizations" />}
        {!loading && error && <EmptyState title="Couldn't load supply authorizations" message={error} />}
        {!loading && !error && rows && rows.length === 0 && (
          <EmptyState title="No matching records" message="Add a record for a domain or bundle to start tracking its ads.txt/app-ads.txt authorization." />
        )}
        {!loading && !error && rows && rows.length > 0 && (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Identity</th>
                  <th>File</th>
                  <th>Expected seller</th>
                  <th>Relationship</th>
                  <th>Status</th>
                  <th>Last checked</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const expanded = expandedId === row.id;
                  return (
                    <Fragment key={row.id}>
                      <tr className="data-table__row--expandable" onClick={() => setExpandedId(expanded ? null : row.id)}>
                        <td className="cell-title">
                          <span className={`disclosure${expanded ? ' disclosure--open' : ''}`} aria-hidden="true">&#9656;</span>
                          {row.identity_value}
                          <div className="cell-subtitle">{row.identity_type}{row.publisher_name ? ` · ${row.publisher_name}` : ''}{row.seat_name ? ` · ${row.seat_name}` : ''}</div>
                        </td>
                        <td className="cell-muted">{row.file_type}</td>
                        <td className="cell-muted">{row.expected_seller_id || '—'}</td>
                        <td className="cell-muted">{row.expected_relationship || '—'}</td>
                        <td><StatusBadge status={row.status} /></td>
                        <td className="cell-muted">{row.last_checked_at ? new Date(row.last_checked_at).toLocaleString() : 'Never'}</td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <button className="btn btn--ghost btn--sm" disabled={rechecking === row.id} onClick={() => recheck(row)}>
                            {rechecking === row.id ? 'Checking…' : 'Recheck'}
                          </button>{' '}
                          <button className="btn btn--ghost btn--sm" onClick={() => setEditing(row)}>Edit</button>{' '}
                          <button className="btn btn--ghost btn--sm" onClick={() => deleteRow(row)}>Delete</button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr className="data-table__expansion-row">
                          <td colSpan={7}>
                            <SupplyAuthDetail row={row} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {creating && (
        <SupplyAuthModal sellerAccounts={sellerAccounts} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />
      )}
      {editing && (
        <SupplyAuthModal sellerAccounts={sellerAccounts} existing={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function SupplyAuthDetail({ row }) {
  return (
    <div className="placement-expansion__block">
      <h3 className="placement-expansion__heading">Last check result</h3>
      {row.check_url && <p className="card__hint">Check URL: <code className="code-chip">{row.check_url}</code></p>}
      {row.notes && <p className="card__hint">Notes: {row.notes}</p>}
      {row.last_check_result ? (
        <pre className="diff-view">{JSON.stringify(row.last_check_result, null, 2)}</pre>
      ) : (
        <p className="card__hint">Never checked -- use "Recheck" above to run a real fetch of this identity's own ads.txt/app-ads.txt.</p>
      )}
    </div>
  );
}

function SupplyAuthModal({ sellerAccounts, existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [identityType, setIdentityType] = useState(existing?.identity_type || 'domain');
  const [identityValue, setIdentityValue] = useState(existing?.identity_value || '');
  const [expectedSellerAccountId, setExpectedSellerAccountId] = useState(existing?.expected_seller_account_id ? String(existing.expected_seller_account_id) : '');
  const [expectedRelationship, setExpectedRelationship] = useState(existing?.expected_relationship || '');
  const [checkUrl, setCheckUrl] = useState(existing?.check_url || '');
  const [notes, setNotes] = useState(existing?.notes || '');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    if (!isEdit) {
      if (!identityValue.trim()) {
        setFormError('identity_value is required.');
        return;
      }
      if (identityType === 'bundle' && !checkUrl.trim()) {
        setFormError('check_url is required for a bundle identity -- there is no reliable way to auto-derive it from a bundle ID alone.');
        return;
      }
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patchSupplyAuthorization(existing.id, {
          expected_seller_account_id: expectedSellerAccountId ? Number(expectedSellerAccountId) : null,
          expected_relationship: expectedRelationship || null,
          check_url: checkUrl.trim() || null,
          notes: notes.trim() || null,
        });
      } else {
        await api.createSupplyAuthorization({
          identity_type: identityType,
          identity_value: identityValue.trim(),
          expected_seller_account_id: expectedSellerAccountId ? Number(expectedSellerAccountId) : null,
          expected_relationship: expectedRelationship || null,
          check_url: checkUrl.trim() || null,
          notes: notes.trim() || null,
        });
      }
      onSaved();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not save record.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={isEdit ? `Edit ${existing.identity_value}` : 'Add supply authorization'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        {!isEdit && (
          <>
            <div className="field">
              <span className="field__label">Identity type</span>
              <div className="checkbox-row">
                <label className="field field--checkbox field--checkbox-inline">
                  <input type="radio" checked={identityType === 'domain'} onChange={() => setIdentityType('domain')} />
                  <span>Domain (ads.txt)</span>
                </label>
                <label className="field field--checkbox field--checkbox-inline">
                  <input type="radio" checked={identityType === 'bundle'} onChange={() => setIdentityType('bundle')} />
                  <span>App bundle (app-ads.txt)</span>
                </label>
              </div>
            </div>
            <label className="field">
              <span className="field__label">Identity value</span>
              <input value={identityValue} onChange={(e) => setIdentityValue(e.target.value)} placeholder={identityType === 'domain' ? 'example.com' : 'com.example.app'} autoFocus />
            </label>
          </>
        )}
        <label className="field">
          <span className="field__label">Expected seller account <span className="field__optional">(optional)</span></span>
          <select value={expectedSellerAccountId} onChange={(e) => setExpectedSellerAccountId(e.target.value)}>
            <option value="">None</option>
            {sellerAccounts.map((sa) => <option key={sa.id} value={sa.id}>{sa.seller_id}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Expected relationship <span className="field__optional">(optional)</span></span>
          <select value={expectedRelationship} onChange={(e) => setExpectedRelationship(e.target.value)}>
            <option value="">Not set</option>
            {RELATIONSHIP_VALUES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field__label">
            Check URL {(isEdit ? existing.identity_type : identityType) === 'bundle' ? '' : <span className="field__optional">(optional -- auto-derived as https://&lt;domain&gt;/ads.txt)</span>}
          </span>
          <input value={checkUrl} onChange={(e) => setCheckUrl(e.target.value)} placeholder="https://developer-domain.com/app-ads.txt" />
        </label>
        <label className="field">
          <span className="field__label">Notes <span className="field__optional">(optional)</span></span>
          <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
        {formError && <div className="form-error">{formError}</div>}
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
}
